import { useSubscription } from '@meraki-internal/state';
import { ForwardIcon } from '../../components/icons/Icons.tsx';
import { EditableMissionState } from '../model/EditableMissionState.ts';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { AlertPresenter } from '../../app/AlertBinder.tsx';
import { OperatorState } from '../../profile/OperatorState.ts';
import { IonItem, IonLabel } from '@ionic/react';
import { Color } from '../../components/common/index.ts';
import { TrackingService } from '../../support/tracking/TrackingService.ts';
import { LAANCAuthorizationTextProvider } from '../laanc-authorization/LAANCAuthorizationTextProvider.ts';

export const AuthorizationsButton = ({ mission, laancIconColor }: { mission: EditableMissionState, laancIconColor: Color }) => {
    const tracker = useInstance(TrackingService);

    const operatorState = useInstance(OperatorState);
    useSubscription(() => operatorState);
    useSubscription(() => mission);

    const { showAlert } = useInstance(AlertPresenter);

    const authorizationTextProvider = useInstance(LAANCAuthorizationTextProvider);

    const relevantAuthorizations = mission.getRelevantAuthorizations();

    const canFly = mission.getCanFly();

    const onClickAuthorization = () => {
        // get overall laanc status for mission
        const laancStatus = mission.getAPLAANCStatus();

        tracker.track('Authorization Viewed', () => ({ canFly, laancStatus }));

        if (laancStatus === 'authorized') {
            mission.pages.laancAuthorization.go();
        } else if (['authorization-failed'].includes(laancStatus!)) {
            showAlert({
                header: 'LAANC Authorization Not Valid',
                message: 'We failed to obtain LAANC Authorization for this mission.',
            });
        } else if (['rescinded', 'invalidated'].includes(laancStatus!)) {
            showAlert({
                header: 'LAANC Authorization Not Valid',
                message: 'Per the FAA, LAANC Authorization for this mission is no longer valid.',
            });
        } else if (['user-canceled'].includes(laancStatus!)) {
            showAlert({
                header: 'LAANC Authorization Canceled',
                message: 'You have canceled this mission, so LAANC Authorization for the mission has also been canceled.',
            });
        } else if (['rescind-acked', 'invalidated-acked'].includes(laancStatus!)) {
            showAlert({
                header: 'LAANC Authorization Canceled',
                message: 'You canceled this mission because LAANC Authorization for the mission is no longer valid, per the FAA.',
            });
        } else if (laancStatus === 'authorizing-failed') {
            showAlert({
                header: 'Mission Not Approved',
                message: `LAANC Authorization for this mission failed.`
            });
        } else {
            showAlert({
                header: 'LAANC Authorization Pending',
                message: 'We were not yet able to get LAANC authorization from the FAA. We will retry for up to 10 minutes. Pull down on the mision to check if it has been approved.'
            });
        }
    };

    if (relevantAuthorizations.length === 0){
        return null;
    }

    const authStatuses = relevantAuthorizations.map(authorization => authorizationTextProvider.getAuthorizationSummary({
        authorization,
        endTime: mission.getEndTime()
    }));
    return (
        <IonItem button detail={false} onClick={onClickAuthorization} data-id="authorizations-button">
            <IonLabel style={{marginRight: 0}}>
                <h2>Authorization</h2>
                {authStatuses.map((authStatus, i) => (
                    <p key={i}>{authStatus}</p>
                ))}
            </IonLabel>
            <ForwardIcon slot="end" color="medium" size="medium" />

        </IonItem>
    );
};
